// iOS Web Audio unlock.
//
// iOS/WebKit boots every AudioContext in the "suspended" state and blocks
// programmatic playback until the context is resumed inside a real user
// gesture.
//
// Post-#184 the Mirror's TTS <audio> plays on the NATIVE media route — it is
// NO LONGER routed through this shared AudioContext (useLipSync dropped
// createMediaElementSource). So a suspended context no longer silences the
// voice; TTS playback is independent of this context entirely. The shared
// context's ONLY remaining job is decodeAudioData for the offline RMS lip-sync
// envelope (useLipSync), which works even while the context is suspended.
//
// This module owns the ONE shared AudioContext (useLipSync imports it) and
// installs a one-shot first-gesture listener that resumes it and plays a silent
// buffer to unlock output. Post-#184 that unlock only serves this context's own
// Web Audio use (the offline decode) — not the TTS voice path anymore — so it
// stays purely as a cheap, harmless warm-up: resuming an already-"running"
// context is a no-op, and the silent buffer is inaudible.

let sharedAudioCtx: AudioContext | null = null;

// Returns the process-wide shared AudioContext (lazily created), or null when
// Web Audio is unavailable. webkit-prefixed fallback for older iOS Safari.
export function getSharedAudioContext(): AudioContext | null {
  if (sharedAudioCtx) return sharedAudioCtx;
  try {
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    sharedAudioCtx = new Ctor();
    return sharedAudioCtx;
  } catch (err) {
    console.warn("[iosAudioUnlock] AudioContext unavailable", err);
    return null;
  }
}

let unlockInstalled = false;

// Resume the shared context and play a 1-frame silent buffer through it. Must
// run inside a user-gesture call stack to satisfy the iOS autoplay policy.
function unlock(): void {
  const ctx = getSharedAudioContext();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }
    const buffer = ctx.createBuffer(1, 1, 22050);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);
  } catch (err) {
    console.warn("[iosAudioUnlock] unlock failed", err);
  }
}

// Installs a one-shot listener that unlocks audio on the first user gesture of
// the session. Idempotent — calling it more than once is a no-op. Call it once
// at app boot; the listeners self-remove after the first gesture ({ once: true }).
export function installIOSAudioUnlock(): void {
  if (unlockInstalled) return;
  if (typeof window === "undefined") return;
  unlockInstalled = true;

  const handler = () => {
    unlock();
    window.removeEventListener("touchend", handler);
    window.removeEventListener("pointerdown", handler);
  };

  // touchend + pointerdown cover iOS Safari and the Capacitor WebView; { once }
  // is belt-and-suspenders alongside the manual removeEventListener above.
  window.addEventListener("touchend", handler, { once: true });
  window.addEventListener("pointerdown", handler, { once: true });
}
